import React from 'react'
import { useIntl } from 'react-intl'

import { Lock } from 'lucide-react'

import { type PromotionOption } from '@/components/model/PromotionOption'
import { formatPromotionFee } from '@/components/utils/formatPromotionFee'
import { cn } from '@/lib/utils'

export interface PromotionPlanSummaryProps extends React.HTMLAttributes<HTMLElement> {
  readonly year?: number
  readonly options: ReadonlyArray<PromotionOption>
  readonly createPromotionLink?: React.ReactNode
}

export const PromotionPlanSummary = React.forwardRef<HTMLElement, PromotionPlanSummaryProps>(
  ({ year, options, createPromotionLink, className, ...props }, ref) => {
    const intl = useIntl()
    const plan = options.filter((option) => option.state !== 'available')
    const total = plan.reduce((sum, option) => sum + (option.fee ?? 0), 0)
    const title =
      year === undefined
        ? intl.formatMessage({ id: 'promotions.shop.plan.titleWithoutYear' })
        : intl.formatMessage({ id: 'promotions.shop.plan.title' }, { year })
    return (
      <aside
        ref={ref}
        aria-label={title}
        className={cn('flex flex-col gap-3 rounded-[4px] border border-gray-300 bg-white p-4', className)}
        {...props}>
        <h2 className="font-heading text-lg font-semibold text-gray-900">{title}</h2>
        <ul className="flex flex-col text-base text-gray-900">
          {plan.map((option) => (
            <li key={option.placement} className="flex justify-between gap-3 border-b border-gray-200 py-2.5">
              <span className="flex items-center gap-1.5">
                {option.state === 'locked' && (
                  <Lock
                    className="size-3 shrink-0 text-gray-700"
                    strokeWidth={2.6}
                    role="img"
                    aria-label={intl.formatMessage({ id: 'promotions.shop.locked' })}
                  />
                )}
                {intl.formatMessage({ id: `promotions.shop.options.${option.placement}.title` })}
              </span>
              <span className="whitespace-nowrap">{formatPromotionFee(intl, option.fee)}</span>
            </li>
          ))}
          <li className="flex justify-between gap-3 pt-3 font-bold">
            <span>{intl.formatMessage({ id: 'promotions.shop.plan.total' })}</span>
            <span data-testid="promotion-plan-total" className="whitespace-nowrap">
              {formatPromotionFee(intl, total)}
            </span>
          </li>
        </ul>
        <p className="text-sm leading-[1.4] text-gray-500">
          {intl.formatMessage({ id: 'promotions.shop.plan.taxes' })}
        </p>
        <p className="text-sm leading-[1.4] text-gray-500">
          {intl.formatMessage({ id: 'promotions.shop.plan.removal' })}
        </p>
        {createPromotionLink}
      </aside>
    )
  }
)
